import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonSpinner
} from '@ionic/react';
import { mail, shield, calendar } from 'ionicons/icons';

import { configService } from '../services/ConfigService';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  const [legalContent, setLegalContent] = useState(null);
  const baseMeta = seoService.getPageMeta('home');
  const [loading, setLoading] = useState(true);
  const config = configService.getConfig();
  const appInfo = config?.app || {};

  useEffect(() => {
    loadLegalContent();
  }, []);

  const loadLegalContent = async () => {
    try {
      const response = await fetch('/assets/configs/common/legal.json');
      const data = await response.json();
      setLegalContent(data);
    } catch (error) {
      console.error('Failed to load legal content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Replace placeholders with actual app values
  const replacePlaceholders = (text) => {
    if (!text) return '';
    return text
      .replace(/{APP_NAME}/g, appInfo.name || 'the Application')
      .replace(/{SUPPORT_EMAIL}/g, appInfo.supportEmail || 'support@example.com')
      .replace(/{AUTHOR}/g, appInfo.author || 'the Developer');
  };

  const openSupport = () => {
    const email = appInfo.supportEmail || 'support@example.com';
    window.open(`mailto:${email}?subject=Privacy Inquiry - ${appInfo.name || 'App'}`, '_system');
  };

  if (loading) {
    return (
      <>
        <SEO
          title="Privacy Policy"
          description="Our privacy policy explains how we collect, use, and protect your data."
          ogImage={baseMeta.ogImage}
          noIndex={true}
        />
        <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/about" />
            </IonButtons>
            <IonTitle>Privacy Policy</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="loading-container">
            <IonSpinner />
            <p>Loading...</p>
          </div>
          </IonContent>
        </IonPage>
      </>
    );
  }

  const privacyPolicy = legalContent?.privacyPolicy;

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Our privacy policy explains how we collect, use, and protect your data."
        ogImage={baseMeta.ogImage}
        noIndex={true}
      />
      <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/about" />
          </IonButtons>
          <IonTitle>Privacy Policy</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div className="legal-container">
          {/* Header */}
          <div className="legal-header">
            <div className="legal-icon">
              <IonIcon icon={shield} />
            </div>
            <h1>Privacy Policy</h1>
            <p className="app-name">{appInfo.name || 'Application'}</p>
            {privacyPolicy?.lastUpdated && (
              <div className="last-updated">
                <IonIcon icon={calendar} />
                <span>Last Updated: {privacyPolicy.lastUpdated}</span>
              </div>
            )}
          </div>

          {/* Content Sections */}
          <div className="legal-content">
            {privacyPolicy?.sections?.map((section, index) => (
              <IonCard key={index} className="legal-section-card">
                <IonCardContent>
                  <h2 className="section-title">{section.title}</h2>
                  <div className="section-content">
                    {replacePlaceholders(section.content).split('\n').map((paragraph, pIndex) => (
                      <p key={pIndex}>{paragraph}</p>
                    ))}
                  </div>
                </IonCardContent>
              </IonCard>
            ))}
          </div>

          {/* Contact Section */}
          <div className="contact-section">
            <h3>Questions?</h3>
            <p>If you have any questions about our Privacy Policy, please contact us.</p>
            <IonButton expand="block" onClick={openSupport}>
              <IonIcon icon={mail} slot="start" />
              Contact Us
            </IonButton>
          </div>

          {/* Footer */}
          <div className="legal-footer">
            <p>© {new Date().getFullYear()} {appInfo.author || 'Developer'}. All rights reserved.</p>
          </div>
        </div>
      </IonContent>
    </IonPage>
    </>
  );
}

export default PrivacyPolicy;